import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "network/network";
import { API_HOST_WMS } from "config/api.config";
import { APIendpoints } from "utils/constants/APIendpoints";
import { STATUS_BEGIN } from "utils/constants/others";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import usePayoutsStore from "./usePayoutsStore";
import useCopyToast from "./useCopyToast.js";
import {
  CARRY_FORWARD_NOTE,
  formatMoney,
  isCarryForwardAdjustment,
} from "./payoutsFormat.js";
import {
  PAYOUT_PAGE_SIZE,
  PAYOUTS_TABS,
  PAYMENT_STATUS_FILTERS,
  TRANSACTION_STATUS_FILTERS,
  PAYMENT_STATUS_CODES,
  TRANSACTION_TYPE_CODES,
  OTHER_EXCLUDABLE_TYPES,
  FROM_ENTITY,
  TO_ENTITY_TYPE,
} from "./config";

// The backend reads these as Indian time, so send the day boundaries with the IST offset.
const IST_OFFSET = "+05:30";
const toRFC3339Start = (yyyyMmDd) =>
  yyyyMmDd ? `${yyyyMmDd}T00:00:00${IST_OFFSET}` : undefined;
const toRFC3339End = (yyyyMmDd) =>
  yyyyMmDd ? `${yyyyMmDd}T23:59:59${IST_OFFSET}` : undefined;

const buildPaymentStatusParam = (statusKeys) => {
  if (!Array.isArray(statusKeys) || !statusKeys.length) return undefined;
  const codes = statusKeys.flatMap((k) => PAYMENT_STATUS_CODES[k] || []);
  return codes.length ? codes.join(",") : undefined;
};

const buildTransactionParams = (statusKeys) => {
  if (!Array.isArray(statusKeys) || !statusKeys.length) return {};
  const hasOther = statusKeys.includes("other");
  const specificCodes = statusKeys
    .filter((k) => k !== "other")
    .map((k) => TRANSACTION_TYPE_CODES[k])
    .filter(Boolean);

  if (hasOther) {
    const excluded = OTHER_EXCLUDABLE_TYPES.filter(
      (code) => !specificCodes.includes(code)
    );
    return excluded.length
      ? { excluded_transaction_types: excluded.join(",") }
      : {};
  }
  return specificCodes.length
    ? { transaction_types: specificCodes.join(",") }
    : {};
};

// mobile uses a different (multi-select, date-range) filter shape than
// the desktop single-select draftFilters/appliedFilters model above
const buildMobilePaymentStatusParam = (selectedKeys) => {
  if (!selectedKeys.length) return undefined;
  const codes = selectedKeys.flatMap((k) => PAYMENT_STATUS_CODES[k] || []);
  return codes.length ? codes.join(",") : undefined;
};

const buildMobileTransactionParams = (selectedKeys) => {
  if (!selectedKeys.length) return {};
  const hasOther = selectedKeys.includes("other");
  const specificCodes = selectedKeys
    .filter((k) => k !== "other")
    .map((k) => TRANSACTION_TYPE_CODES[k])
    .filter(Boolean);

  if (hasOther) {
    const excluded = OTHER_EXCLUDABLE_TYPES.filter(
      (code) => !specificCodes.includes(code)
    );
    return excluded.length
      ? { excluded_transaction_types: excluded.join(",") }
      : {};
  }
  return specificCodes.length
    ? { transaction_types: specificCodes.join(",") }
    : {};
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// Offset for a page in api call has +1 to decide hasNextPage in desktop table
const offsetForPage = (pageNumber) => PAYOUT_PAGE_SIZE * pageNumber + 1;

const TAB_PARAM_TO_KEY = {
  payments: PAYOUTS_TABS.PAYMENTS,
  transactions: PAYOUTS_TABS.TRANSACTIONS,
};
const TAB_KEY_TO_PARAM = {
  [PAYOUTS_TABS.PAYMENTS]: "payments",
  [PAYOUTS_TABS.TRANSACTIONS]: "transactions",
};

const EMPTY_FILTERS = { dueDate: "", paidDate: "", status: [] };

const toYyyyMmDd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultDueRange = () => {
  const now = new Date();
  const from = new Date(now);
  from.setDate(now.getDate() - 15);
  const to = new Date(now);
  to.setDate(now.getDate() + 5);
  return { from: toYyyyMmDd(from), to: toYyyyMmDd(to) };
};

const DOWNLOAD_ERROR_MESSAGE =
  "Unable to download payment advice. Please try again.";

const FILE_POLL_INTERVAL_MS = 1000;
const FILE_POLL_MAX_RETRIES = 10;

// The presigned url is issued before the file is uploaded in the background,
// so keep requesting it until the object exists (200), retries run out or the
// signal is aborted (user left the page).
const waitForFile = async (url, signal) => {
  for (let attempt = 0; attempt <= FILE_POLL_MAX_RETRIES; attempt += 1) {
    if (signal.aborted) return false;
    try {
      const result = await fetch(url, { signal });
      if (result.status === 200) {
        result.body?.cancel();
        return true;
      }
    } catch (error) {
      // not available yet (or aborted), retry
    }
    if (attempt < FILE_POLL_MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, FILE_POLL_INTERVAL_MS));
    }
  }
  return false;
};

const DEFAULT_PAGE_SIZE = 50;

export default function usePayoutPageLogic() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { copy: copyUtr, copyToast } = useCopyToast();
  // Filters live in the store so they survive leaving the screen (payment details,
  // other pages) and coming back.
  const { payoutFilters, setPayoutFilter } = usePayoutsStore();

  const [activeTab, setActiveTabState] = useState(
    () =>
      TAB_PARAM_TO_KEY[searchParams.get("tab")] ||
      payoutFilters.activeTab ||
      PAYOUTS_TABS.PAYMENTS
  );
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    setPayoutFilter("activeTab", tab);
  };
  // draft = bound to the filter inputs, applied = what's actually sent to the API
  // (the filters card only takes effect once "Apply filters" is clicked).
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const toEntityId =
    (typeof window !== "undefined" && window.localStorage.getItem(LS_KEYS.OUTLET_ID)) || "";

  const vendorName =
    (typeof window !== "undefined" && window.localStorage.getItem(LS_KEYS.OUTLET_NAME)) || "";

  const {
    accruals,
    accrualsStatus,
    accrualsHasMore,
    accrualsHitCount,
    accrualsNextOffset,
    transactions,
    transactionsStatus,
    transactionsHasMore,
    transactionsHitCount,
    transactionsNextOffset,
    getVendorAccruals,
    getVendorTransactions,
    resetAccruals,
    resetTransactions,
  } = usePayoutsStore();

  const isPayments = activeTab === PAYOUTS_TABS.PAYMENTS;
  const statusFilterOptions = isPayments ? PAYMENT_STATUS_FILTERS : TRANSACTION_STATUS_FILTERS;

  const fetchAccruals = useCallback(
    (offset, append) => {
      getVendorAccruals(
        {
          page_start_offset: offset,
          page_size: PAYOUT_PAGE_SIZE,
          fetch_details: false,
          from_entity_id: FROM_ENTITY.id,
          from_entity_type: FROM_ENTITY.type,
          to_entity_id: toEntityId,
          to_entity_type: TO_ENTITY_TYPE,
          accrual_start_date: toRFC3339Start(appliedFilters.dueDate),
          accrual_end_date: toRFC3339End(appliedFilters.dueDate),
          accrual_statuses: buildPaymentStatusParam(appliedFilters.status),
        },
        { append }
      );
    },
    [toEntityId, appliedFilters, getVendorAccruals]
  );

  const fetchTransactions = useCallback(
    (offset, append) => {
      getVendorTransactions(
        {
          page_start_offset: offset,
          page_size: PAYOUT_PAGE_SIZE,
          entity_id: toEntityId,
          entity_type: TO_ENTITY_TYPE,
          transaction_number: searchQuery,
          consideration_start_date: appliedFilters.dueDate
            ? appliedFilters.dueDate.replace(/-/g, "")
            : undefined,
          consideration_end_date: appliedFilters.dueDate
            ? appliedFilters.dueDate.replace(/-/g, "")
            : undefined,
          ...buildTransactionParams(appliedFilters.status),
        },
        { append }
      );
    },
    [toEntityId, appliedFilters, searchQuery, getVendorTransactions]
  );

  useEffect(() => {
    setPageNumber(0);
    if (isPayments) {
      fetchAccruals(0, false);
    } else {
      fetchTransactions(0, false);
    }
  }, [isPayments, appliedFilters, searchQuery, fetchAccruals, fetchTransactions]);

  const handleTabChange = (tabKey) => {
    if (tabKey === activeTab) return;
    if (tabKey === PAYOUTS_TABS.PAYMENTS) {
      resetTransactions();
      setSearchQuery("");
    } else {
      resetAccruals();
    }
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setActiveTab(tabKey);
    setSearchParams({ tab: TAB_KEY_TO_PARAM[tabKey] }, { replace: true });
  };

  const goToPage = (requestedPageNumber) => {
    const status = isPayments ? accrualsStatus : transactionsStatus;
    if (status === STATUS_BEGIN || requestedPageNumber === pageNumber) return;

    const offset = requestedPageNumber === 0 ? 0 : offsetForPage(requestedPageNumber);
    if (isPayments) {
      fetchAccruals(offset, false);
    } else {
      fetchTransactions(offset, false);
    }
    setPageNumber(requestedPageNumber);
  };

  const setDraftDueDate = (date) =>
    setDraftFilters((prev) => ({ ...prev, dueDate: date ? toYyyyMmDd(date) : "" }));
  const setDraftPaidDate = (date) =>
    setDraftFilters((prev) => ({ ...prev, paidDate: date ? toYyyyMmDd(date) : "" }));
  const setDraftStatus = (statusKey) =>
    setDraftFilters((prev) => {
      const current = Array.isArray(prev.status) ? prev.status : [];
      const next = current.includes(statusKey)
        ? current.filter((k) => k !== statusKey)
        : [...current, statusKey];
      return { ...prev, status: next };
    });

  const applyFilters = () => setAppliedFilters(draftFilters);

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  };

  // --- drawer / row detail, URL-synced ---
  const openPayment = (referenceNumber) => {
    setSearchParams({ tab: TAB_KEY_TO_PARAM[activeTab], reference_number: referenceNumber });
  };

  const openTransaction = (transactionNumber) => {
    setSearchParams({ tab: TAB_KEY_TO_PARAM[activeTab], transaction_number: transactionNumber });
  };

  const closeDrawer = () => {
    setSearchParams({ tab: TAB_KEY_TO_PARAM[activeTab] }, { replace: true });
  };

  const openReferenceNumber = searchParams.get("reference_number");
  const openTransactionNumber = searchParams.get("transaction_number");

  const selectedTransaction = useMemo(() => {
    if (!openTransactionNumber) return null;
    return transactions.find((t) => t.transaction_number === openTransactionNumber) || null;
  }, [openTransactionNumber, transactions]);

  const isPaymentsLoading = accrualsStatus === STATUS_BEGIN;
  const isTransactionsLoading = transactionsStatus === STATUS_BEGIN;
  const isLoading = isPayments ? isPaymentsLoading : isTransactionsLoading;
  // The table pages by a fixed size. accrualsHasMore / transactionsHasMore only say the
  // last page was not empty, so use the total (or a full page when there is none).
  const currentHitCount = isPayments ? accrualsHitCount : transactionsHitCount;
  const currentRowCount = isPayments ? accruals.length : transactions.length;
  const hasNextPage =
    currentHitCount != null
      ? (pageNumber + 1) * PAYOUT_PAGE_SIZE < currentHitCount
      : currentRowCount >= PAYOUT_PAGE_SIZE;

  const paymentsTableRowData = useMemo(
    () =>
      accruals.map((item, idx) => ({
        id: item.reference_number || `accrual-${idx}`,
        dueDate: formatDate(item.accrual_date),
        amount: formatMoney(item.payout_amount, { spaced: true }),
        isDebit: Number(item.payout_amount) < 0,
        utr: item.payout_utr_number || "-",
        remarks:
          item.payout_utr_number || !item.tag
            ? "-"
            : isCarryForwardAdjustment(item)
              ? CARRY_FORWARD_NOTE
              : "-",
        tag: item.tag,
        _raw: item,
      })),
    [accruals]
  );

  const transactionsTableRowData = useMemo(
    () =>
      transactions.map((item, idx) => ({
        id: item.transaction_number || `txn-${idx}`,
        transactionType:
          item.display_transaction_type ||
          item.parent_transaction_type ||
          item.transaction_type ||
          "Invoice",
        number: item.transaction_number,
        remarks: item.consideration_date
          ? `Due ${formatDate(item.consideration_date)}`
          : item.actual_payment_date
            ? `Paid on ${formatDate(item.actual_payment_date)}`
            : "-",
        amount: formatMoney(item.transaction_amount, { spaced: true }),
        tag: item.tag,
        _raw: item,
      })),
    [transactions]
  );

  // --- mobile-only state: multi-select filter chips, date-range chip, search, scroll ---
  const mobilePaymentsFilter = payoutFilters.paymentsFilter;
  const mobileTransactionsFilter = payoutFilters.transactionsFilter;
  const setMobilePaymentsFilter = (value) => setPayoutFilter("paymentsFilter", value);
  const setMobileTransactionsFilter = (value) =>
    setPayoutFilter("transactionsFilter", value);
  // stays null in the store until the user picks a range, so the default window is fresh each visit
  const [defaultDueRange] = useState(getDefaultDueRange);
  const mobileDueRange = payoutFilters.dueRange || defaultDueRange;
  const setMobileDueRange = (value) => setPayoutFilter("dueRange", value);

  const mobileSearchTerm = payoutFilters.searchTerm;
  const setMobileSearchTerm = (value) => setPayoutFilter("searchTerm", value);
  // a restored search term reopens the search box with the term filled in
  const [mobileSearchOpen, setMobileSearchOpen] = useState(Boolean(mobileSearchTerm));
  const [mobileSearchDraft, setMobileSearchDraft] = useState(mobileSearchTerm);
  const mobileSearchInputRef = useRef(null);

  useEffect(() => {
    if (mobileSearchOpen) mobileSearchInputRef.current?.focus();
  }, [mobileSearchOpen]);

  const [selectedMobileTransaction, setSelectedMobileTransaction] = useState(null);
  const mobileScrollRef = useRef(null);
  const lastMobileScrollTopRef = useRef(0);

  const mobileActiveFilter = isPayments ? mobilePaymentsFilter : mobileTransactionsFilter;
  const setMobileFilter = isPayments ? setMobilePaymentsFilter : setMobileTransactionsFilter;

  const toggleMobileFilter = (key) => {
    setMobileFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const removeMobileFilter = (key, e) => {
    e.stopPropagation();
    setMobileFilter((prev) => prev.filter((k) => k !== key));
  };

  const closeMobileSearch = () => {
    setMobileSearchOpen(false);
    setMobileSearchDraft("");
    setMobileSearchTerm("");
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    setMobileSearchTerm(mobileSearchDraft.trim());
    // dismiss the mobile keyboard once the search is submitted
    document.activeElement?.blur();
  };

  const switchMobileTab = (nextTab) => {
    if (nextTab === activeTab) return;
    if (nextTab === PAYOUTS_TABS.PAYMENTS) {
      resetTransactions();
    } else {
      resetAccruals();
    }
    closeMobileSearch();
    setActiveTab(nextTab);
  };

  // Searching by transaction number looks across all dates and types, so the due
  // range and the type chips are left out of that request.
  const buildMobileTransactionFilterParams = () =>
    mobileSearchTerm
      ? {}
      : {
          consideration_start_date: mobileDueRange.from
            ? mobileDueRange.from.replace(/-/g, "")
            : undefined,
          consideration_end_date: mobileDueRange.to
            ? mobileDueRange.to.replace(/-/g, "")
            : undefined,
          ...buildMobileTransactionParams(mobileTransactionsFilter),
        };

  useEffect(() => {
    if (activeTab === PAYOUTS_TABS.PAYMENTS) {
      getVendorAccruals(
        {
          page_start_offset: 0,
          page_size: DEFAULT_PAGE_SIZE,
          fetch_details: false,
          from_entity_id: FROM_ENTITY.id,
          from_entity_type: FROM_ENTITY.type,
          to_entity_id: toEntityId,
          to_entity_type: TO_ENTITY_TYPE,
          accrual_start_date: toRFC3339Start(mobileDueRange.from),
          accrual_end_date: toRFC3339End(mobileDueRange.to),
          accrual_statuses: buildMobilePaymentStatusParam(mobilePaymentsFilter),
        },
        { append: false }
      );
    } else {
      getVendorTransactions(
        {
          page_start_offset: 0,
          page_size: DEFAULT_PAGE_SIZE,
          entity_id: toEntityId,
          entity_type: TO_ENTITY_TYPE,
          transaction_number: mobileSearchTerm,
          ...buildMobileTransactionFilterParams(),
        },
        { append: false }
      );
    }
  }, [activeTab, mobileDueRange, mobilePaymentsFilter, mobileTransactionsFilter, mobileSearchTerm, toEntityId, getVendorAccruals, getVendorTransactions]);

  const handleMobileScroll = useCallback(() => {
    const el = mobileScrollRef.current;
    if (!el) return;
    // Only scrolling down loads more. When a request ends the loader row disappears,
    // the list shrinks and the browser pulls scrollTop up; that must not count as
    // reaching the bottom again, or a failed request would be retried in a loop.
    const scrollingDown = el.scrollTop > lastMobileScrollTopRef.current;
    lastMobileScrollTopRef.current = el.scrollTop;
    if (!scrollingDown) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (!nearBottom) return;

    if (activeTab === PAYOUTS_TABS.PAYMENTS) {
      if (accrualsStatus === STATUS_BEGIN || !accrualsHasMore) return;
      getVendorAccruals(
        {
          page_start_offset: accrualsNextOffset,
          page_size: DEFAULT_PAGE_SIZE,
          fetch_details: false,
          from_entity_id: FROM_ENTITY.id,
          from_entity_type: FROM_ENTITY.type,
          to_entity_id: toEntityId,
          to_entity_type: TO_ENTITY_TYPE,
          accrual_start_date: toRFC3339Start(mobileDueRange.from),
          accrual_end_date: toRFC3339End(mobileDueRange.to),
          accrual_statuses: buildMobilePaymentStatusParam(mobilePaymentsFilter),
        },
        { append: true }
      );
    } else {
      if (transactionsStatus === STATUS_BEGIN || !transactionsHasMore) return;
      getVendorTransactions(
        {
          page_start_offset: transactionsNextOffset,
          page_size: DEFAULT_PAGE_SIZE,
          entity_id: toEntityId,
          entity_type: TO_ENTITY_TYPE,
          transaction_number: mobileSearchTerm,
          ...buildMobileTransactionFilterParams(),
        },
        { append: true }
      );
    }
  }, [
    activeTab,
    accrualsNextOffset,
    accrualsStatus,
    accrualsHasMore,
    transactionsNextOffset,
    transactionsStatus,
    transactionsHasMore,
    toEntityId,
    mobileDueRange,
    mobilePaymentsFilter,
    mobileTransactionsFilter,
    mobileSearchTerm,
    getVendorAccruals,
    getVendorTransactions,
  ]);

  const mobileItems = isPayments ? accruals : transactions;
  // total from the first page of the current filters; rows loaded so far until it arrives
  const mobileHitCount = isPayments ? accrualsHitCount : transactionsHitCount;
  const mobileCountLabel = useMemo(() => {
    const n = mobileHitCount ?? (mobileItems?.length || 0);
    return isPayments ? `${n} payments` : `${n} transactions`;
  }, [mobileHitCount, mobileItems, isPayments]);

  // --- shared: payment advice download (both mobile and desktop) ---
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const downloadAbortRef = useRef(null);

  // stop polling (and further state updates) if the user leaves mid-download
  useEffect(() => () => downloadAbortRef.current?.abort(), []);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError("");
    const controller = new AbortController();
    downloadAbortRef.current = controller;
    const downloadDueRange = isPayments ? mobileDueRange : { from: appliedFilters.dueDate, to: appliedFilters.dueDate };
    try {
      const response = await api.get(
        API_HOST_WMS,
        APIendpoints.downloadVendorAccrualTransactions,
        {
          query: {
            from_entity_id: FROM_ENTITY.id,
            from_entity_type: FROM_ENTITY.type,
            to_entity_id: toEntityId,
            to_entity_type: TO_ENTITY_TYPE,
            accrual_start_date: toRFC3339Start(downloadDueRange.from),
            accrual_end_date: toRFC3339End(downloadDueRange.to),
            fetch_details: false,
            business_entity: FROM_ENTITY.id,
            is_detailed_view_required: true,
          },
        }
      );
      const presignedUrl = response?.data?.response?.presigned_url;
      const ready =
        presignedUrl && (await waitForFile(presignedUrl, controller.signal));
      if (controller.signal.aborted) return;
      if (!ready) {
        setDownloadError(DOWNLOAD_ERROR_MESSAGE);
      } else {
        // a plain link click downloads the file in place, no new tab and no
        // popup blocker involved even though it runs after the polling wait
        const link = document.createElement("a");
        link.href = presignedUrl;
        link.click();
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Payment advice download failed", error);
      setDownloadError(
        error?.response?.data?.error?.message || DOWNLOAD_ERROR_MESSAGE
      );
    } finally {
      if (!controller.signal.aborted) setDownloading(false);
    }
  };

  return {
    // shared
    activeTab,
    handleTabChange,
    isPayments,
    searchQuery,
    setSearchQuery,
    draftFilters,
    setDraftDueDate,
    setDraftPaidDate,
    setDraftStatus,
    statusFilterOptions,
    applyFilters,
    clearFilters,
    pageNumber,
    hasNextPage,
    goToPage,
    isLoading,
    paymentsTableRowData,
    transactionsTableRowData,
    openPayment,
    openTransaction,
    closeDrawer,
    openReferenceNumber,
    selectedTransaction,
    vendorName,
    downloading,
    downloadError,
    setDownloadError,
    handleDownload,

    // mobile-only
    mobileActiveFilter,
    toggleMobileFilter,
    removeMobileFilter,
    mobileDueRange,
    setMobileDueRange,
    mobileSearchOpen,
    setMobileSearchOpen,
    mobileSearchDraft,
    setMobileSearchDraft,
    mobileSearchInputRef,
    closeMobileSearch,
    handleMobileSearchSubmit,
    switchMobileTab,
    handleMobileScroll,
    mobileScrollRef,
    copyUtr,
    copyToast,
    mobileItems,
    mobileCountLabel,
    selectedMobileTransaction,
    setSelectedMobileTransaction,
  };
}
